import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useSiteConfig, useScreenSize } from '@/hooks';
import { storage, getUID, encode, decode, C } from '@/utils';
import socket from '@/utils/socketInstance';
import { fetchPragmaticSlots, fetchGisGames, launchJsGame } from '@/services/api';
import Banner from '@/components/home/Banner/Banner';
import SkylaBannerSlider from '@/components/home/SkylaBannerSlider/SkylaBannerSlider';
import Bonus500Banner from '@/components/home/Bonus500Banner/Bonus500Banner';
import HotGames from '@/components/home/HotGames/HotGames';
import LatestWins from '@/components/home/LatestWins/LatestWins';
import Sportsbook from '@/components/home/Sportsbook/Sportsbook';
import GameCards from '@/components/home/GameCards/GameCards';
import Sports from '@/components/home/Sports/Sports';
import GamingCards from '@/components/home/GamingCards/GamingCards';
import Leaderboard from '@/components/home/Leaderboard/Leaderboard';
import PaymentBanner from '@/components/home/PaymentBanner/PaymentBanner';
import Jackpot from '@/components/home/Jackpot/Jackpot';
import styles from './Home.module.css';

/**
 * Home Page Component
 * Main landing page with all sections
 */
const Home = () => {
  const { siteConfig, isLoading } = useSiteConfig();
  const { isMobile } = useScreenSize();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const socketListenerRef = useRef(null);
  const hasSetupSocketRef = useRef(false);

  // Fetch jsPragmeticSlots data using React Query with organized service (like old code)
  // This fetches Pragmatic Play slots games for LatestWins component
  const { data: jsPragmeticSlots = [], isLoading: isLoadingSlots } = useQuery({
    queryKey: ['jsPragmeticSlots', isMobile],
    queryFn: () => fetchPragmaticSlots(1, 20, isMobile),
    staleTime: 300000, // 5 minutes cache
    retry: 2,
  });

  // Fetch games from specific providers for LatestWins
  const latestWinsProviders = ['Betsoft', 'Igrosoft', 'OneTouch', 'BFGames', 'BoomingGames'];
  const latestWinsQueries = useQueries({
    queries: latestWinsProviders.map((provider) => ({
      queryKey: ['latestWinsGames', provider, isMobile],
      queryFn: () => fetchGisGames(provider, 1, 20, '', isMobile),
      staleTime: 300000, // 5 minutes cache
      retry: 2,
    })),
  });

  // Combine all games from LatestWins providers
  const latestWinsGames = useMemo(() => {
    const allGames = [];
    latestWinsQueries.forEach((query) => {
      if (query.data?.games && Array.isArray(query.data.games)) {
        // Map games to match expected structure
        query.data.games.forEach((game) => {
          allGames.push({
            id: game.uuid,
            uuid: game.uuid,
            game_uid: game.uuid,
            name: game.name,
            game_name: game.name,
            game_icon: game.image,
            image: game.image,
            game_type: game.type,
            provider: game.provider || game.providerName,
            source: 'gis',
          });
        });
      }
    });
    return allGames;
  }, [latestWinsQueries]);

  const isLoadingLatestWins = latestWinsQueries.some((query) => query.isLoading);

  useEffect(() => {
    // Check login status
    const logged = storage.getKey('logged') === true || localStorage.getItem('logged') === 'true';
    setIsLoggedIn(logged);
  }, []);

  // Setup socket listeners for user info (like old code)
  useEffect(() => {
    // Prevent multiple socket setups
    if (hasSetupSocketRef.current) {
      return;
    }

    let isMounted = true;
    hasSetupSocketRef.current = true;

    // Only setup socket if user is logged in
    const uid = getUID();
    if (!uid) {
      return;
    }

    const handleUserInfo = (data) => {
      if (!isMounted) return;
      try {
        const decodedData = decode(data);
        if (decodedData && decodedData.status) {

          console.log('decodedData', decodedData);
          setUserData(decodedData);
        }
      } catch (error) {
        console.error('Error decoding user info:', error);
      }
    };

    // Setup socket listener (like old code - socket is always available)
    socket.on(C.USER_INFO, handleUserInfo);
    socketListenerRef.current = handleUserInfo;

    // Request user info
    socket.emit(C.USER_INFO, encode({
      id: uid,
      coin: storage.getKey('coin') || '',
      rate: null,
      game: 'all',
      first: true,
    }));

    // Cleanup
    return () => {
      isMounted = false;
      hasSetupSocketRef.current = false;
      if (socket && socketListenerRef.current) {
        try {
          socket.off(C.USER_INFO, socketListenerRef.current);
          socketListenerRef.current = null;
        } catch (error) {
          console.error('Error cleaning up socket listener:', error);
        }
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className={styles.home}>
        <div className={styles.loadingContainer}>
          <div className={styles.loader}>
            <span className={styles.loaderText}>loading</span>
            <span className={styles.load}></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.home}>
      {/* Banner/Slider Section */}
      <section className={styles.section}>
        {isLoggedIn ? (
          <SkylaBannerSlider />
        ) : (
          <Banner isLoggedIn={isLoggedIn} />
        )}
      </section>


      {/* Recent Big Wins Section */}
      <section className={styles.section}>
        <LatestWins
          all={latestWinsGames}
          slots={latestWinsGames}
          liveCasino={latestWinsGames}
          providers={latestWinsProviders}
          isLoading={isLoadingLatestWins}
        />
      </section>

      {/* Gaming Cards Section (BC Originals) */}
      <section className={styles.section}>
        <GamingCards />
      </section>


      {/* Hot Games Section */}
      <section className={styles.section}>
        <HotGames />
      </section>

      {/* Jackpot Section */}
      {/* <section className={styles.section}>
        <Jackpot />
      </section> */}

       {/* Live Casino Section */}
       <section className={styles.section}>
        <GameCards
          title="Live Casino"
          providers={['Evolution', 'Ezugi', 'Pragmatic Play', 'Microgaming', 'Platipus', 'OneTouch']}
        />
      </section>

      {/* Sportsbook Section */}
      {siteConfig?.sports && (
        <section className={styles.section}>
          <Sportsbook />
        </section>
      )} 


      {/* Bonus 500 Banner Section */}
  

    <Bonus500Banner/>
     

      {/* Live Sports Section */}
      {/* {siteConfig?.sports && (
        <section className={styles.section}>
          <Sports />
        </section>
      )} */}

      {/* Slots Section (Pragmatic slots only) */}
      <section className={styles.section}>
        <GameCards
          title="Popular Slots"
          fetchFunction={fetchPragmaticSlots}
          launchFunction={launchJsGame}
          queryKey="pragmaticSlots"
        />
      </section>

      {/* Crash Section */}
      <section className={styles.section}>
        <GameCards
          title="Crash Games"
          providers={['Spribe', 'Jili']}
        />
      </section>


        {/* Payment Banner Section */}
        <section className={styles.section}>
        <PaymentBanner />
      </section>
      {/* Live Casino Section */}
      <section className={styles.section}>
        <GameCards
          title="Game Shows"
          providers={['Betsoft']}
        />
      </section>



      {/* Leaderboard Section */}
      <section className={styles.section}>
        <Leaderboard />
      </section>
    </div>
  );
};

export default Home;
